import { atom } from "jotai";
import { floorToString } from "../utils/mimosysDataExtracter";
import { Trend } from "../types";
import dayjs, { Dayjs } from "dayjs";
import { voislogApi } from "../utils/apiClients/voislogApi";
import { ApiError } from "../utils/apiClients/ApiError";
import { fetchAllPaginated } from "../utils/fetchAllPaginated";

export type ResultChartData = {
  date: number;
  vitality100?: number;
  mental_activity100?: number;
  mental_activity_reliability?: boolean;
  mi1_result?: number;
  mi1_result_reliability?: boolean;
  datingType?: string;
};

export type ChartInfoType = {
  data: ResultChartData[];
  latestVitalityTrend: Trend;
  vitalityAve: number;
  vitalitySdTop: number;
  vitalitySdBottom: number;

  latestMentalActivityTrend: Trend;
  mentalActivityAve: number;
  mentalActivitySdTop: number;
  mentalActivitySdBottom: number;

  mi1Ave: number;
  mi1NormalRangeTop: number;
  mi1NormalRangeBottom: number;

  recordingLastDates: number;
  outputStenScore: boolean;
};

export interface MimosysDateType {
  recorded_at: string;
  analyzed_at: string;
  mental_activity: number;
  mental_activity_reliability?: boolean;
  vitality: number;
  mi1_result?: number;
  mi1_result_reliability?: boolean;
  file_id: string;
}

export interface MimosysResultDateType {
  recorded_at: string;
  mental_activity: number;
  mental_activity_reliability?: boolean;
  vitality: number;
  mi1_result?: number;
  mi1_result_reliability?: boolean;
}

export const analysisStartedDateAtom = atom<Dayjs | null>((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  if (mimosysData.length > 0) {
    return dayjs(mimosysData[0].recorded_at);
  } else {
    return null;
  }
});

export const latestDateAtom = atom<Dayjs | null>((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  if (mimosysData.length > 0) {
    return dayjs(mimosysData[mimosysData.length - 1].recorded_at);
  } else {
    return null;
  }
});

export const latestStenScoreAtom = atom((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  return mimosysData.length &&
    mimosysData[mimosysData.length - 1].mi1_result !== undefined
    ? (mimosysData[mimosysData.length - 1].mi1_result as number)
    : null;
});

export const latestVitalityAtom = atom((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  return mimosysData.length > 0
    ? mimosysData[mimosysData.length - 1].vitality
    : 0;
});
export const latestVitalityStringAtom = atom((get) => {
  return floorToString(get(latestVitalityAtom));
});

export const latestMentalActivityAtom = atom((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  return mimosysData.length > 0
    ? mimosysData[mimosysData.length - 1].mental_activity
    : 0;
});

export const latestMentalActivityStringAtom = atom((get) => {
  return floorToString(get(latestMentalActivityAtom));
});

export const latestMi1ResultReliabilityAtom = atom((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  return mimosysData.length > 0
    ? mimosysData[mimosysData.length - 1].mi1_result_reliability
    : undefined;
});

export const latestMentalActivityReliabilityAtom = atom((get) => {
  const mimosysData = get(MimosysDataAscAtom);
  return mimosysData.length > 0
    ? mimosysData[mimosysData.length - 1].mental_activity_reliability
    : undefined;
});

export const MimosysDataInitial: MimosysDateType[] = [];
export const MimosysDataAtom = atom(MimosysDataInitial);

/** recorded_at 昇順 */
export const MimosysDataAscAtom = atom((get) => {
  const data = get(MimosysDataAtom);
  return [...data].sort(
    (a, b) =>
      new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime(),
  );
});

export const isGetMimosysDataAtom = atom(false);

export type EmotionType = {
  ang: number;
  joy: number;
  clm: number;
  srw: number;
  exc: number;
};

export const latestEmotionAtom = atom<EmotionType>({
  ang: 0,
  joy: 0,
  clm: 0,
  srw: 0,
  exc: 0,
});

/** Mi-1解析結果一覧を全件取得し、recorded_at 昇順の MimosysDateType[] に変換して返す */
export async function fetchMimosysDataList(): Promise<MimosysDateType[]> {
  const mi1List = await fetchAllPaginated("/v2/core/analysis/mi1/list", {
    params: { query: { detail: false } },
  });
  return mi1List
    .map((item) => ({
      recorded_at: item.recorded_at,
      analyzed_at: item.analyzed_at,
      mental_activity: item.analysis_result.mental_activity,
      mental_activity_reliability:
        item.analysis_result.mental_activity_reliability ?? undefined,
      vitality: item.analysis_result.vitality,
      mi1_result: item.analysis_result.mi1_result ?? undefined,
      mi1_result_reliability:
        item.analysis_result.mi1_result_reliability ?? undefined,
      file_id: item.recording_id,
    }))
    .sort((a, b) => {
      if (dayjs(a.recorded_at).isBefore(dayjs(b.recorded_at))) return -1;
      if (dayjs(a.recorded_at).isAfter(dayjs(b.recorded_at))) return 1;
      return 0;
    });
}

type fetchEmotionsProps = {
  recording_id: string;
};

type fetchEmotionsValue = {
  emotions: EmotionType[];
};

export async function fetchEmotions({
  recording_id,
}: fetchEmotionsProps): Promise<fetchEmotionsValue | null> {
  const { data, error, response } = await voislogApi.GET(
    "/v2/core/analysis/mi1/recordings/{recording_id}",
    {
      params: { path: { recording_id } },
    },
  );
  if (error) throw new ApiError(response, "get", error);
  if (!data.detailed_analysis_result) return null;
  return {
    emotions: data.detailed_analysis_result.emotions,
  };
}

export async function fetchLatestEmotions(): Promise<fetchEmotionsValue | null> {
  const { data, error, response } = await voislogApi.GET(
    "/v2/core/analysis/mi1/list",
    {
      params: { query: { detail: true, limit: 1 } },
    },
  );
  if (error) throw new ApiError(response, "get", error);
  if (data.length === 0) return null;
  return {
    emotions: data[0].detailed_analysis_result!.emotions,
  };
}
